/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * MyCoach - Dedicated page for students to view their coach information,
 * assignments, announcements, and messages
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Video,
  Calendar,
  MessageSquare,
  Mail,
  Loader2,
  ExternalLink,
  Bell,
  Check,
  X,
  Clock,
  Settings,
  Shield,
  Eye,
  BookOpen,
  BarChart3,
  FileSignature,
  UserPlus,
  RefreshCw
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import StudentCoachMessages from '../components/StudentCoachMessages'
import { Avatar } from '../components/profile'
import messagingService from '../services/messagingService'
import coachService from '../services/coachService'
import api from '../services/api'
import toast from 'react-hot-toast'
import TutoringAgreementModal from '../components/tutoring/TutoringAgreementModal'
import { RELATIONSHIP_STATUS_BADGE as STATUS_BADGE } from '../utils/relationshipBadges'
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus'
import { createLogger } from '../utils/logger'

const logger = createLogger('MyCoach')

const MyCoach = () => {
  const navigate = useNavigate()
  const { user, myCoaches, fetchMyCoaches } = useAuthStore()
  // The 2-party agreement: a minor student does NOT sign; their guardian signs in their place.
  const viewerIsMinor = (() => {
    if (!user?.dateOfBirth) return false
    const age = Math.floor(
      (Date.now() - new Date(user.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)
    )
    return age < 18
  })()
  const [selectedCoachId, setSelectedCoachId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [processingInvitation, setProcessingInvitation] = useState(null)
  const [pendingAgreements, setPendingAgreements] = useState([])
  const [signingAgreement, setSigningAgreement] = useState(null)
  const [remindingGuardian, setRemindingGuardian] = useState(false)
  const [resendingInvite, setResendingInvite] = useState(false)
  // Whether the student has a parent/guardian linked. null = unknown (not yet loaded). Drives whether
  // we offer "Remind guardian" (linked) or steer them to "Invite a guardian" (none linked).
  const [hasLinkedGuardian, setHasLinkedGuardian] = useState(null)
  // A pending (emailed-but-not-accepted) guardian invite, if one exists. Lets us offer "Resend" + the
  // masked address instead of "Invite a guardian" when one is already in flight. null = none/unknown.
  const [pendingGuardianInvite, setPendingGuardianInvite] = useState(null)

  // Re-notify the student's linked guardian(s) about a coaching request awaiting their approval.
  const handleRemindGuardian = async (relationshipId) => {
    setRemindingGuardian(true)
    try {
      const res = await coachService.remindGuardian(relationshipId)
      toast.success(res?.message || 'Reminder sent to your guardian.')
    } catch (err) {
      toast.error(err.message || 'Could not send the reminder.')
    } finally {
      setRemindingGuardian(false)
    }
  }

  // Resend a pending guardian invitation whose email may not have arrived.
  const handleResendGuardianInvite = async (invitationId) => {
    if (!invitationId) return
    setResendingInvite(true)
    try {
      const res = await coachService.resendInvitation(invitationId)
      toast.success(res?.message || 'Invitation resent to your guardian.')
    } catch (err) {
      toast.error(err.message || 'Could not resend the invitation.')
    } finally {
      setResendingInvite(false)
    }
  }

  // Tutoring-agreement modal state (relationshipId of the coach whose agreement we're signing)

  // Permissions modal state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [selectedCoachForPermissions, setSelectedCoachForPermissions] = useState(null)
  const [coachPermissions, setCoachPermissions] = useState({
    canViewProgress: true,
    canManageChallenges: true,
    canViewAnalytics: true,
    canSendMessages: true
  })
  const [savingPermissions, setSavingPermissions] = useState(false)

  // Fetch coaches and pending invitations
  const loadData = async () => {
    setLoading(true)
    try {
      await fetchMyCoaches()
      // Fetch pending invitations
      const invitationsRes = await api.get('/user/coach-invitations')
      if (invitationsRes.data.success) {
        setPendingInvitations(invitationsRes.data.received || [])
      }
      // Fetch relationships awaiting the student's agreement signature
      const agreementsRes = await api.get('/user/coach-pending-agreements')
      if (agreementsRes.data.success) {
        setPendingAgreements(agreementsRes.data.pendingAgreements || [])
      }
      // Determine the guardian state from the role-aware coaching-actions endpoint, which emits exactly
      // one of: `remind_guardian` (a guardian is linked), `resend_guardian_invite` (an invite is pending
      // but not accepted, carrying the invitationId + masked email), or `add_guardian` (none at all).
      try {
        const actionsRes = await api.get('/user/coaching-actions')
        const items = actionsRes.data?.data?.items || []
        const resendItem = items.find((i) => i.type === 'resend_guardian_invite')
        if (items.some((i) => i.type === 'remind_guardian')) {
          setHasLinkedGuardian(true)
        } else if (resendItem || items.some((i) => i.type === 'add_guardian')) {
          setHasLinkedGuardian(false)
        }
        setPendingGuardianInvite(
          resendItem
            ? {
                invitationId: resendItem.invitationId,
                guardianEmailMasked: resendItem.guardianEmailMasked
              }
            : null
        )
      } catch {
        // Leave as unknown; the remind action still works and reports if no guardian is linked.
      }
    } catch (error) {
      logger.error('Error loading coach data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Relationships where the student still needs to sign their own acknowledgment.
  const agreementsToSign = pendingAgreements.filter((pa) =>
    (pa.readiness?.missing || []).some((m) => m.code === 'STUDENT_ACK')
  )

  // Reusable section prompting the student to sign their acknowledgment. Rendered in both the
  // "no active coaches" branch (a pending_agreements student has no ACTIVE coach yet) and the main
  // page (mixed active + pending).
  const renderAgreementsToSign = () => {
    if (agreementsToSign.length === 0) return null
    return (
      <div className="card mb-6 border border-primary-200 dark:border-primary-800 bg-gradient-to-br from-primary-50/50 to-indigo-50/50 dark:from-primary-900/10 dark:to-indigo-900/10">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-primary-100 dark:bg-primary-900/40 rounded-xl">
              <FileSignature className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Action needed: sign your agreement
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sign your acknowledgment so tutoring can begin
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {agreementsToSign.map((pa) => (
              <div
                key={pa.relationshipId}
                className="flex flex-col gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 sm:flex-row sm:items-center sm:gap-4"
              >
                <Avatar
                  type={pa.coach?.avatarType || 'dicebear'}
                  seed={pa.coach?.avatarSeed}
                  style={pa.coach?.avatarStyle || 'bottts'}
                  name={getCoachDisplayName(pa.coach || {})}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {getCoachDisplayName(pa.coach || {})}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">will be your coach</p>
                </div>
                <button
                  onClick={() => setSigningAgreement({ relationshipId: pa.relationshipId })}
                  className="btn btn-sm btn-primary whitespace-nowrap self-start sm:self-auto"
                >
                  <FileSignature className="w-4 h-4 mr-1" />
                  Sign acknowledgment
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    loadData()
  }, [])

  // Refresh when returning to the tab so a relationship that activated elsewhere (e.g. the guardian
  // signed) stops showing stale "pending" without a manual reload.
  useRefetchOnFocus(() => {
    if (!loading) loadData()
  })

  // Fetch unread message count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const response = await messagingService.getUnreadCount()
        if (response.success) {
          setUnreadCount(response.unreadCount || 0)
        }
      } catch (error) {
        logger.error('Error fetching unread count:', error)
      }
    }
    fetchUnread()
  }, [])

  // Coaches shown in the main view: accepted relationships (pending_agreements + active). Not-yet
  // accepted (pending) relationships are surfaced separately in the Pending Invitations banner.
  const coaches = myCoaches.filter((c) => c.relationshipStatus !== 'pending')

  // Set default selected coach when coaches are loaded
  useEffect(() => {
    if (coaches.length > 0 && !selectedCoachId) {
      setSelectedCoachId(coaches[0].id)
    }
  }, [coaches, selectedCoachId])

  const selectedCoach = coaches.find((c) => c.id === selectedCoachId)
  const selectedActive = selectedCoach?.relationshipStatus === 'active'
  // Whether the student (the viewer) has already signed their own agreement for this relationship.
  const viewerSigned = !!selectedCoach?.signedRoles?.includes('student')
  // Show the "sign" CTA only when the relationship needs agreements, the viewer is an adult student
  // (minors don't sign — their guardian does), and they haven't signed yet.
  const studentMustSign =
    selectedCoach?.relationshipStatus === 'pending_agreements' && !viewerIsMinor && !viewerSigned
  // A minor waiting on their guardian (to authorize the coach or sign the agreement): show the
  // remind/invite-guardian actions.
  const minorAwaitingGuardian =
    viewerIsMinor &&
    (selectedCoach?.relationshipStatus === 'pending_guardian_approval' ||
      selectedCoach?.relationshipStatus === 'pending_agreements')

  // Handle accepting coach invitation
  const handleAcceptInvitation = async (relationshipId) => {
    setProcessingInvitation(relationshipId)
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/accept`)
      if (response.data.success) {
        toast.success('Coach invitation accepted!')
        loadData() // Refresh data
      }
    } catch (error) {
      logger.error('Error accepting invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to accept invitation')
    } finally {
      setProcessingInvitation(null)
    }
  }

  // Handle declining coach invitation
  const handleDeclineInvitation = async (relationshipId) => {
    setProcessingInvitation(relationshipId)
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/decline`)
      if (response.data.success) {
        toast.success('Invitation declined')
        loadData() // Refresh data
      }
    } catch (error) {
      logger.error('Error declining invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to decline invitation')
    } finally {
      setProcessingInvitation(null)
    }
  }

  // Get display name for a coach
  const getCoachDisplayName = (coach) => {
    if (coach.firstName || coach.lastName) {
      return `${coach.firstName || ''} ${coach.lastName || ''}`.trim()
    }
    return coach.publicName || coach.email || 'Coach'
  }

  // Handle opening permissions modal
  const handleOpenPermissions = (coach) => {
    setSelectedCoachForPermissions(coach)
    setCoachPermissions(
      coach.permissions || {
        canViewProgress: true,
        canManageChallenges: true,
        canViewAnalytics: true,
        canSendMessages: true
      }
    )
    setShowPermissionsModal(true)
  }

  // Handle saving coach permissions
  const handleSavePermissions = async () => {
    if (!selectedCoachForPermissions) return

    // Ensure at least one permission is enabled
    const anyEnabled = Object.values(coachPermissions).some((v) => v)
    if (!anyEnabled) {
      toast.error('At least one permission must be enabled')
      return
    }

    setSavingPermissions(true)
    try {
      const response = await coachService.updateMyCoachPermissions(
        selectedCoachForPermissions.id,
        coachPermissions
      )
      if (response.success) {
        toast.success('Permissions updated')
        setShowPermissionsModal(false)
        setSelectedCoachForPermissions(null)
        loadData() // Refresh to update coach data
      }
    } catch (error) {
      logger.error('Error saving permissions:', error)
      toast.error(error.response?.data?.message || 'Failed to save permissions')
    } finally {
      setSavingPermissions(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading coach information...</span>
        </div>
      </div>
    )
  }

  // Show pending invitations page if no accepted coaches but have invitations
  if (coaches.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">My Coach</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage your coaching relationships
            </p>
          </div>

          {/* Agreements awaiting the student's signature */}
          {renderAgreementsToSign()}

          {/* Pending Invitations */}
          {pendingInvitations.length > 0 && (
            <div className="card mb-6">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Pending Invitations
                  </h2>
                  <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                    {pendingInvitations.length}
                  </span>
                </div>

                <div className="space-y-3">
                  {pendingInvitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex items-center gap-4 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg"
                    >
                      <Avatar
                        type={invitation.coach?.avatarType || 'dicebear'}
                        seed={invitation.coach?.avatarSeed}
                        style={invitation.coach?.avatarStyle || 'bottts'}
                        name={getCoachDisplayName(invitation.coach || {})}
                        size="md"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {getCoachDisplayName(invitation.coach || {})}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          wants to be your coach
                        </p>
                        {invitation.coach?.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                            {invitation.coach.email}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleAcceptInvitation(invitation.id)}
                          disabled={processingInvitation === invitation.id}
                          className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
                        >
                          {processingInvitation === invitation.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineInvitation(invitation.id)}
                          disabled={processingInvitation === invitation.id}
                          className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                        >
                          <X className="w-4 h-4" />
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {pendingInvitations.length === 0 && agreementsToSign.length === 0 && (
            <div className="text-center py-12">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No Active Coaches
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                You don't have any active coaching relationships yet.
              </p>
            </div>
          )}
        </div>

        <TutoringAgreementModal
          isOpen={!!signingAgreement}
          relationshipId={signingAgreement?.relationshipId}
          signerRole="student"
          onClose={() => setSigningAgreement(null)}
          onSigned={() => loadData()}
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {coaches.length > 1 ? 'My Coaches' : 'My Coach'}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            View your coaching announcements and messages
          </p>
        </div>

        {/* Agreements awaiting the student's signature */}
        {renderAgreementsToSign()}

        {/* Pending Invitations Banner (when user has coaches but also new invitations) */}
        {pendingInvitations.length > 0 && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <h3 className="font-medium text-amber-800 dark:text-amber-200">
                New Coaching Invitation{pendingInvitations.length > 1 ? 's' : ''}
              </h3>
            </div>
            <div className="space-y-2">
              {pendingInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg"
                >
                  <Avatar
                    type={invitation.coach?.avatarType || 'dicebear'}
                    seed={invitation.coach?.avatarSeed}
                    style={invitation.coach?.avatarStyle || 'bottts'}
                    name={getCoachDisplayName(invitation.coach || {})}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {getCoachDisplayName(invitation.coach || {})}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      wants to be your coach
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAcceptInvitation(invitation.id)}
                      disabled={processingInvitation === invitation.id}
                      className="btn btn-primary btn-sm"
                    >
                      {processingInvitation === invitation.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Accept'
                      )}
                    </button>
                    <button
                      onClick={() => handleDeclineInvitation(invitation.id)}
                      disabled={processingInvitation === invitation.id}
                      className="btn btn-secondary btn-sm"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coach Tabs (only if multiple coaches) */}
        {coaches.length > 1 && (
          <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-1 overflow-x-auto pb-px">
              {coaches.map((coach) => (
                <button
                  key={coach.id}
                  onClick={() => setSelectedCoachId(coach.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    selectedCoachId === coach.id
                      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Avatar
                    type={coach.avatarType || 'dicebear'}
                    seed={coach.avatarSeed}
                    style={coach.avatarStyle || 'bottts'}
                    name={getCoachDisplayName(coach)}
                    size="xs"
                  />
                  {getCoachDisplayName(coach)}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedCoach && (
          <div className="space-y-6">
            {/* Coach Info Card */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-start gap-4">
                  <Avatar
                    type={selectedCoach.avatarType || 'dicebear'}
                    seed={selectedCoach.avatarSeed}
                    style={selectedCoach.avatarStyle || 'bottts'}
                    name={getCoachDisplayName(selectedCoach)}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {getCoachDisplayName(selectedCoach)}
                      </h2>
                      {STATUS_BADGE[selectedCoach.relationshipStatus] && (
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE[selectedCoach.relationshipStatus].cls}`}
                        >
                          {STATUS_BADGE[selectedCoach.relationshipStatus].label}
                        </span>
                      )}
                    </div>
                    {selectedCoach.coachProfile?.headline && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                        {selectedCoach.coachProfile.headline}
                      </p>
                    )}
                    {selectedCoach.email && (
                      <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <Mail className="w-4 h-4" />
                        <span>{selectedCoach.email}</span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {selectedCoach.coachZoomLink && (
                        <a
                          href={selectedCoach.coachZoomLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                        >
                          <Video className="w-4 h-4" />
                          Zoom Meeting
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {selectedCoach.coachCalendlyLink && (
                        <a
                          href={selectedCoach.coachCalendlyLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                        >
                          <Calendar className="w-4 h-4" />
                          Schedule
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {selectedActive && selectedCoach.permissions?.canSendMessages !== false && (
                        <button
                          onClick={() => navigate('/messages')}
                          className="btn btn-primary btn-sm inline-flex items-center gap-1.5 relative"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Message
                          {unreadCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center">
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </button>
                      )}
                      {studentMustSign && (
                        <button
                          onClick={() =>
                            setSigningAgreement({ relationshipId: selectedCoach.relationshipId })
                          }
                          className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
                        >
                          <FileSignature className="w-4 h-4" />
                          Sign agreement
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenPermissions(selectedCoach)}
                        className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                        title="Manage permissions"
                      >
                        <Settings className="w-4 h-4" />
                        Permissions
                      </button>
                    </div>
                  </div>
                </div>

                {/* Coach Bio */}
                {selectedCoach.coachProfile?.bio && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedCoach.coachProfile.bio}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Agreements-required notice (relationship not yet active) */}
            {!selectedActive && (
              <div className="card">
                <div className="card-body flex items-start gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <FileSignature className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {minorAwaitingGuardian
                        ? hasLinkedGuardian === false
                          ? 'Add a parent/guardian to continue'
                          : 'Waiting on your parent/guardian'
                        : viewerSigned
                          ? 'Waiting on the other parties'
                          : 'Finish setting up tutoring'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                      {minorAwaitingGuardian
                        ? hasLinkedGuardian === false
                          ? "You don't have a parent or guardian linked yet. A guardian must approve before tutoring can begin — invite one to continue."
                          : selectedCoach.relationshipStatus === 'pending_guardian_approval'
                            ? "Your parent/guardian needs to approve this coach before tutoring can begin. We've emailed them — you can send a reminder."
                            : 'Your parent/guardian signs the tutoring agreement on your behalf. Tutoring activates once your coach and guardian have signed — you can send a reminder.'
                        : viewerSigned
                          ? 'You have signed your tutoring agreement. Tutoring will activate once your coach (and guardian, if required) have signed.'
                          : 'Sign your tutoring agreement to activate this coaching relationship. Coaching features unlock once all parties have signed.'}
                    </p>
                    {studentMustSign && (
                      <button
                        onClick={() =>
                          setSigningAgreement({ relationshipId: selectedCoach.relationshipId })
                        }
                        className="btn btn-primary btn-sm inline-flex items-center gap-1.5 mt-3"
                      >
                        <FileSignature className="w-4 h-4" />
                        Sign agreement
                      </button>
                    )}
                    {viewerSigned && (
                      <button
                        onClick={() =>
                          setSigningAgreement({ relationshipId: selectedCoach.relationshipId })
                        }
                        className="btn btn-secondary btn-sm inline-flex items-center gap-1.5 mt-3"
                      >
                        <FileSignature className="w-4 h-4" />
                        View agreement status
                      </button>
                    )}
                    {/* Minor waiting on a guardian — exactly one of three states:
                        (1) a guardian is linked → nudge them; (2) an invite is pending but not yet
                        accepted → resend it (email may not have arrived) or invite a different guardian;
                        (3) no guardian at all → invite one. */}
                    {minorAwaitingGuardian &&
                      (hasLinkedGuardian !== false ? (
                        // (1) Linked guardian (or still unknown): re-notify them to approve/sign.
                        <div className="mt-3">
                          <button
                            onClick={() => handleRemindGuardian(selectedCoach.relationshipId)}
                            disabled={remindingGuardian}
                            className="btn btn-primary btn-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                          >
                            {remindingGuardian ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Bell className="w-4 h-4" />
                            )}
                            Remind guardian
                          </button>
                        </div>
                      ) : pendingGuardianInvite ? (
                        // (2) Invite pending (not accepted). Show who it went to, and let them resend or
                        // invite a different guardian (a second one — family orgs allow up to two).
                        <div className="mt-3">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            We emailed{' '}
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {pendingGuardianInvite.guardianEmailMasked}
                            </span>{' '}
                            — they haven&apos;t accepted yet. The email can take a minute; you can
                            resend it or invite a different guardian.
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              onClick={() =>
                                handleResendGuardianInvite(pendingGuardianInvite.invitationId)
                              }
                              disabled={resendingInvite}
                              className="btn btn-primary btn-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                            >
                              {resendingInvite ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                              Resend invitation
                            </button>
                            <button
                              onClick={() =>
                                navigate('/profile?action=invite-guardian#organization')
                              }
                              className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                            >
                              <UserPlus className="w-4 h-4" />
                              Invite a different guardian
                            </button>
                          </div>
                        </div>
                      ) : (
                        // (3) No guardian linked and none invited: invite one.
                        <div className="mt-3">
                          <button
                            onClick={() => navigate('/profile?action=invite-guardian#organization')}
                            className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
                          >
                            <UserPlus className="w-4 h-4" />
                            Invite a guardian
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* Announcements Section (active relationships only) */}
            {selectedActive && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Announcements
                  </h3>
                </div>
                <StudentCoachMessages coachId={selectedCoachId} showEmpty />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Permissions Modal */}
      {showPermissionsModal && selectedCoachForPermissions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Coach Permissions
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {getCoachDisplayName(selectedCoachForPermissions)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPermissionsModal(false)
                    setSelectedCoachForPermissions(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Control what this coach can access for your account:
              </p>

              <div className="space-y-4">
                {/* View Progress */}
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Eye className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        View Progress
                      </span>
                    </div>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canViewProgress}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canViewProgress: !prev.canViewProgress
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canViewProgress
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canViewProgress ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
                    See challenge history, scores, and learning progress
                  </p>
                </div>

                {/* Manage Challenges */}
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        Manage Challenges
                      </span>
                    </div>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canManageChallenges}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canManageChallenges: !prev.canManageChallenges
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canManageChallenges
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canManageChallenges ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
                    View detailed question responses
                  </p>
                </div>

                {/* View Analytics */}
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        View Analytics
                      </span>
                    </div>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canViewAnalytics}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canViewAnalytics: !prev.canViewAnalytics
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canViewAnalytics
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canViewAnalytics ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
                    Access detailed performance analytics and trends
                  </p>
                </div>

                {/* Send Messages */}
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        Send Messages
                      </span>
                    </div>
                    <button
                      role="switch"
                      aria-checked={coachPermissions.canSendMessages}
                      onClick={() =>
                        setCoachPermissions((prev) => ({
                          ...prev,
                          canSendMessages: !prev.canSendMessages
                        }))
                      }
                      disabled={savingPermissions}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                        coachPermissions.canSendMessages
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${coachPermissions.canSendMessages ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
                    Communicate directly with you
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowPermissionsModal(false)
                  setSelectedCoachForPermissions(null)
                }}
                className="flex-1 btn btn-secondary"
                disabled={savingPermissions}
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                className="flex-1 btn btn-primary inline-flex items-center justify-center gap-2"
              >
                {savingPermissions ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <TutoringAgreementModal
        isOpen={!!signingAgreement}
        relationshipId={signingAgreement?.relationshipId}
        signerRole="student"
        onClose={() => setSigningAgreement(null)}
        onSigned={() => loadData()}
      />
    </div>
  )
}

export default MyCoach
